'use client';

import React, { useMemo, useState, useEffect } from 'react';
import { apiCall } from '@/app/messages/_lib/apiCall';
import { useVoiceCall } from './useVoiceCall';
import { VoiceDevicePicker } from './VoiceDevicePicker';
import { VoiceQualityPicker } from './VoiceQualityPicker';
import { VoiceParticipantList } from './VoiceParticipantList';

// Комментарий на русском: главная панель голосового чата

interface VoicePanelProps {
  roomId: number;
  currentUserId: number;
  onClose?: () => void;
  onError?: (error: string) => void;
}

export function VoicePanel({ roomId, currentUserId, onClose, onError }: VoicePanelProps) {
  const [showSettings, setShowSettings] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteQuery, setInviteQuery] = useState('');
  const [inviteBusy, setInviteBusy] = useState(false);
  const [roomMembers, setRoomMembers] = useState<any[]>([]);

  const {
    callState,
    isConnecting,
    isMuted,
    isDeafened,
    audioLevel,
    availableMics,
    selectedMicId,
    voiceSettings,
    effectiveQuality,
    qualityHint,
    webrtcStats,
    startCall,
    joinCall,
    leaveCall,
    toggleMute,
    toggleDeafen,
    changeMicrophone,
    loadMicrophones,
    inviteUser,
    setPreferredQuality,
    setAudioProcessing,
    setAutoQualityEnabled,
  } = useVoiceCall({
    roomId,
    currentUserId,
    onError,
    onCallEnded: onClose,
  });

  // Загрузка микрофонов при открытии
  useEffect(() => {
    loadMicrophones();
  }, [loadMicrophones]);

  const isInCall = callState.participants.some(p => p.user_id === currentUserId);
  const canInvite = callState.active && isInCall;

  const filteredMembers = useMemo(() => {
    const q = inviteQuery.trim().toLowerCase();
    const arr = Array.isArray(roomMembers) ? roomMembers : [];
    if (!q) return arr;
    return arr.filter((m: any) => {
      const u = m?.user || {};
      const name = String(u.display_name || u.username || '').toLowerCase();
      const uname = String(u.username || '').toLowerCase();
      return name.includes(q) || uname.includes(q);
    });
  }, [inviteQuery, roomMembers]);

  useEffect(() => {
    if (!inviteOpen) return;
    if (!roomId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await apiCall(`/api/chat/rooms/${roomId}/members/`);
        const data = await res.json().catch(() => []);
        if (!res.ok) return;
        if (cancelled) return;
        setRoomMembers(Array.isArray(data) ? data : []);
      } catch {
        // ignore
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [inviteOpen, roomId]);

  return (
    <div className="flex flex-col bg-site-surface dark:bg-site-bg rounded-xl shadow-xl overflow-hidden max-w-sm w-full">
      {/* Заголовок */}
      <div className="flex items-center justify-between px-4 py-3 bg-site-raised border-b border-site-border">
        <div className="flex items-center gap-2">
          <VoiceIcon className="w-5 h-5 text-site-text" />
          <span className="font-medium text-site-text">Голосовой чат</span>
          {callState.active && (
            <span className="px-2 py-0.5 text-xs bg-site-text/20 text-site-text rounded-full">
              {callState.participants.length}/50
            </span>
          )}
        </div>
        <button
          onClick={onClose}
          className="p-1 hover:bg-site-text/20 rounded-lg transition-colors"
        >
          <svg className="w-5 h-5 text-site-text" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Контент */}
      <div className="p-4 flex flex-col gap-4">
        {/* Индикатор уровня громкости при активном звонке */}
        {isInCall && (
          <div className="h-2 bg-site-raised dark:bg-site-raised rounded-full overflow-hidden">
            <div
              className="h-full bg-site-success transition-all duration-75"
              style={{ width: `${Math.min(audioLevel * 100, 100)}%` }}
            />
          </div>
        )}

        {/* Настройки (перед звонком или по кнопке) */}
        {(showSettings || !callState.active) && (
          <div className="flex flex-col gap-3">
            <VoiceDevicePicker
              devices={availableMics}
              selectedId={selectedMicId}
              onChange={changeMicrophone}
              disabled={isConnecting}
            />
            <VoiceQualityPicker
              value={voiceSettings.preferred_quality}
              onChange={setPreferredQuality}
              disabled={isConnecting}
            />
            <div className="flex items-center justify-between gap-2">
              <div className="text-xs text-site-muted dark:text-site-muted">Авто-адаптация качества</div>
              <Toggle
                label={voiceSettings.auto_quality_enabled ? 'вкл' : 'выкл'}
                title="Быстро ухудшаем, медленно улучшаем (пока по сети, без SFU stats)"
                value={!!voiceSettings.auto_quality_enabled}
                disabled={isConnecting}
                onChange={(v) => void setAutoQualityEnabled(v)}
              />
            </div>
            {(voiceSettings.preferred_quality === 'auto' || voiceSettings.auto_quality_enabled) && (
              <div className="text-[11px] text-site-muted dark:text-site-muted">
                Авто: {labelQuality(effectiveQuality)}
                {qualityHint ? ` · ${qualityHint}` : ''}
              </div>
            )}
            {webrtcStats?.hint && (
              <div className="text-[11px] text-site-muted dark:text-site-muted">
                Связь: {webrtcStats.hint}
              </div>
            )}

            <div className="grid grid-cols-3 gap-2">
              <Toggle
                label="Шум"
                title="Шумоподавление"
                value={!!voiceSettings.noise_suppression}
                disabled={isConnecting}
                onChange={(v) => void setAudioProcessing({ noise_suppression: v, echo_cancellation: !!voiceSettings.echo_cancellation, auto_gain_control: !!voiceSettings.auto_gain_control })}
              />
              <Toggle
                label="Эхо"
                title="Эхоподавление"
                value={!!voiceSettings.echo_cancellation}
                disabled={isConnecting}
                onChange={(v) => void setAudioProcessing({ noise_suppression: !!voiceSettings.noise_suppression, echo_cancellation: v, auto_gain_control: !!voiceSettings.auto_gain_control })}
              />
              <Toggle
                label="AGC"
                title="Автогейн (авто-усиление)"
                value={!!voiceSettings.auto_gain_control}
                disabled={isConnecting}
                onChange={(v) => void setAudioProcessing({ noise_suppression: !!voiceSettings.noise_suppression, echo_cancellation: !!voiceSettings.echo_cancellation, auto_gain_control: v })}
              />
            </div>
          </div>
        )}

        {/* Список участников */}
        {callState.active && (
          <VoiceParticipantList
            participants={callState.participants}
            currentUserId={currentUserId}
          />
        )}

        {/* Приглашение участников */}
        {canInvite && (
          <div className="flex flex-col gap-2">
            <button
              onClick={() => setInviteOpen(v => !v)}
              className="w-full px-4 py-2.5 rounded-xl bg-site-raised dark:bg-site-surface text-site-muted dark:text-site-text hover:bg-site-raised dark:hover:bg-site-raised transition-colors"
            >
              {inviteOpen ? 'Скрыть приглашение' : 'Пригласить участника'}
            </button>

            {inviteOpen && (
              <div className="rounded-xl border border-site-border dark:border-site-border overflow-hidden">
                <div className="p-2">
                  <input
                    value={inviteQuery}
                    onChange={(e) => setInviteQuery(e.target.value)}
                    placeholder="Поиск по имени/логину"
                    className="w-full px-3 py-2 text-sm rounded-lg border border-site-border dark:border-site-border bg-site-surface dark:bg-site-bg text-site-muted dark:text-site-text focus:ring-2 focus:ring-site-accent focus:border-transparent"
                  />
                </div>
                <div className="max-h-48 overflow-y-auto">
                  {filteredMembers
                    .filter((m: any) => Number(m?.user?.id || 0) !== currentUserId)
                    .slice(0, 30)
                    .map((m: any) => {
                      const u = m?.user || {};
                      const uid = Number(u.id || 0) || 0;
                      const name = String(u.display_name || u.username || 'Пользователь');
                      const alreadyInCall = callState.participants.some(p => p.user_id === uid);
                      return (
                        <button
                          key={uid}
                          type="button"
                          disabled={!uid || inviteBusy || alreadyInCall}
                          onClick={async () => {
                            if (!uid) return;
                            setInviteBusy(true);
                            try {
                              const ok = await inviteUser(uid);
                              if (ok) onError?.('Приглашение отправлено');
                            } finally {
                              setInviteBusy(false);
                            }
                          }}
                          className={`w-full px-3 py-2 text-left flex items-center justify-between gap-2 hover:bg-site-raised dark:hover:bg-site-surface transition-colors ${
                            alreadyInCall ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                        >
                          <span className="text-sm text-site-muted dark:text-site-text truncate">{name}</span>
                          <span className="text-xs text-site-muted dark:text-site-muted">
                            {alreadyInCall ? 'в звонке' : 'пригласить'}
                          </span>
                        </button>
                      );
                    })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Кнопки управления */}
        <div className="flex flex-col gap-2">
          {!callState.active ? (
            // Нет активного звонка - можно начать
            <button
              onClick={() => startCall()}
              disabled={isConnecting}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 
                       bg-site-success hover:bg-site-success disabled:bg-site-success
                       text-site-text font-medium rounded-xl transition-colors"
            >
              {isConnecting ? (
                <>
                  <LoadingSpinner />
                  Подключение...
                </>
              ) : (
                <>
                  <PhoneIcon className="w-5 h-5" />
                  Начать звонок
                </>
              )}
            </button>
          ) : isInCall ? (
            // В звонке - управление
            <>
              <div className="flex gap-2">
                <button
                  onClick={toggleMute}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 
                            font-medium rounded-xl transition-colors
                            ${isMuted
                              ? 'bg-site-danger hover:bg-site-danger text-site-on-accent'
                              : 'bg-site-raised dark:bg-site-surface hover:bg-site-raised dark:hover:bg-site-raised text-site-muted dark:text-site-text'
                            }`}
                >
                  {isMuted ? <MicOffIcon className="w-5 h-5" /> : <MicIcon className="w-5 h-5" />}
                  {isMuted ? 'Вкл. микрофон' : 'Выкл. микрофон'}
                </button>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={toggleDeafen}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 
                            text-sm font-medium rounded-xl transition-colors
                            ${isDeafened
                              ? 'bg-site-danger hover:bg-site-danger text-site-on-accent'
                              : 'bg-site-raised dark:bg-site-surface hover:bg-site-raised dark:hover:bg-site-raised text-site-muted dark:text-site-text'
                            }`}
                >
                  {isDeafened ? <HeadphonesOffIcon className="w-4 h-4" /> : <HeadphonesIcon className="w-4 h-4" />}
                  {isDeafened ? 'Вкл. звук' : 'Выкл. звук'}
                </button>

                <button
                  onClick={() => setShowSettings(!showSettings)}
                  className={`px-3 py-2.5 rounded-xl transition-colors
                            ${showSettings
                              ? 'bg-site-accent text-site-on-accent'
                              : 'bg-site-raised dark:bg-site-surface text-site-muted dark:text-site-text hover:bg-site-raised dark:hover:bg-site-raised'
                            }`}
                >
                  <SettingsIcon className="w-4 h-4" />
                </button>
              </div>

              <button
                onClick={leaveCall}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 
                         bg-site-danger hover:bg-site-danger text-site-on-accent font-medium rounded-xl transition-colors"
              >
                <PhoneOffIcon className="w-5 h-5" />
                Покинуть звонок
              </button>
            </>
          ) : (
            // Активный звонок, но не участвует - можно присоединиться
            <button
              onClick={() => joinCall()}
              disabled={isConnecting}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 
                       bg-site-accent hover:bg-site-accent disabled:bg-site-accent
                       text-site-text font-medium rounded-xl transition-colors"
            >
              {isConnecting ? (
                <>
                  <LoadingSpinner />
                  Подключение...
                </>
              ) : (
                <>
                  <PhoneIcon className="w-5 h-5" />
                  Присоединиться
                </>
              )}
            </button>
          )}
        </div>

        {/* Информация о звонке */}
        {callState.active && callState.started_by && (
          <div className="text-xs text-center text-site-muted dark:text-site-muted">
            Начал: {callState.started_by.display_name}
          </div>
        )}
      </div>
    </div>
  );
}

// Иконки
function VoiceIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
    </svg>
  );
}

function PhoneIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
    </svg>
  );
}

function PhoneOffIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M16 8l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2M5 3a2 2 0 00-2 2v1c0 8.284 6.716 15 15 15h1a2 2 0 002-2v-3.28a1 1 0 00-.684-.948l-4.493-1.498a1 1 0 00-1.21.502l-1.13 2.257a11.042 11.042 0 01-5.516-5.517l2.257-1.128a1 1 0 00.502-1.21L9.228 3.683A1 1 0 008.279 3H5z" />
    </svg>
  );
}

function MicIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
    </svg>
  );
}

function MicOffIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
    </svg>
  );
}

function HeadphonesIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
    </svg>
  );
}

function HeadphonesOffIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M5.636 5.636a9 9 0 1012.728 12.728M5.636 5.636L18.364 18.364" />
    </svg>
  );
}

function SettingsIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function LoadingSpinner() {
  return (
    <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
    </svg>
  );
}

export default VoicePanel;

function Toggle({
  label,
  title,
  value,
  disabled,
  onChange,
}: {
  label: string;
  title: string;
  value: boolean;
  disabled?: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <button
      type="button"
      title={title}
      disabled={disabled}
      onClick={() => onChange(!value)}
      className={`px-2 py-2 rounded-xl text-xs font-medium transition-colors border
        ${value
          ? 'bg-site-accent text-site-on-accent border-site-accent'
          : 'bg-site-raised dark:bg-site-surface text-site-muted dark:text-site-text border-site-border dark:border-site-border hover:bg-site-raised dark:hover:bg-site-raised'
        }
        disabled:opacity-50 disabled:cursor-not-allowed`}
    >
      {label}
    </button>
  );
}

function labelQuality(q: string) {
  if (q === 'low') return 'низкое';
  if (q === 'high') return 'высокое';
  return 'среднее';
}
